/**
 * 主题管理 Composable - 已简化，强制浅色模式
 *
 * @module useTheme
 * @description 已禁用暗色主题支持，强制使用浅色模式
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue';

// 全局单例状态 - 强制为 false (浅色模式)
const isDarkTheme = ref(false);

export interface ThemeOptions {
  enableSystemDetection?: boolean;
  enableTransition?: boolean;
  storageKey?: string;
}

export interface UseThemeReturn {
  // 状态
  isDarkTheme: Ref<boolean>;
  currentTheme: ComputedRef<'light' | 'dark'>;

  // 方法
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  detectSystemTheme: () => 'light' | 'dark';

  // 工具方法
  applyTheme: (theme: 'light' | 'dark') => void;
  saveThemePreference: (theme: 'light' | 'dark') => void;
  loadThemePreference: () => 'light' | 'dark' | null;
}

// 计算属性 - 强制浅色
const currentTheme = computed(() => 'light' as const);

/**
 * 主题管理 Hook - 已简化，强制浅色模式
 *
 * @param {ThemeOptions} _options - 配置选项（已忽略）
 * @returns {UseThemeReturn} 主题管理状态和方法
 */
export function useTheme(_options: ThemeOptions = {}): UseThemeReturn {
  /**
   * 检测系统主题偏好 - 已禁用，始终返回浅色
   */
  const detectSystemTheme = (): 'light' | 'dark' => {
    return 'light';
  };

  /**
   * 应用主题到DOM - 强制浅色
   */
  const applyTheme = (_theme: 'light' | 'dark') => {
    if (typeof document === 'undefined') return;
    // 确保移除 dark 类
    document.documentElement.classList.remove('dark');
  };

  /**
   * 保存主题偏好 - 强制浅色
   */
  const saveThemePreference = (_theme: 'light' | 'dark') => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem('theme', 'light');
    } catch (error) {
      console.warn('无法保存主题偏好:', error);
    }
  };

  /**
   * 从本地存储加载主题偏好 - 强制返回浅色
   */
  const loadThemePreference = (): 'light' | 'dark' | null => {
    return 'light';
  };

  /**
   * 设置主题 - 已禁用，强制浅色
   */
  const setTheme = (_theme: 'light' | 'dark') => {
    isDarkTheme.value = false;
    applyTheme('light');
    saveThemePreference('light');
  };

  /**
   * 切换主题 - 已禁用
   */
  const toggleTheme = () => {
    // 不执行任何操作，保持浅色模式
  };

  // 初始化时确保是浅色模式
  if (typeof document !== 'undefined') {
    document.documentElement.classList.remove('dark');
  }

  return {
    // 状态
    isDarkTheme,
    currentTheme,

    // 方法
    toggleTheme,
    setTheme,
    detectSystemTheme,

    // 工具方法
    applyTheme,
    saveThemePreference,
    loadThemePreference
  };
}
